import { Action, Alert, ButtonGroup, Card, Icon } from 'corso-admin-ui';
import {
  automationsDisplayName,
  eventTypeDescription,
  Pegasus,
} from 'corso-types';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import GridList from '~/components/GridList';
import { AutomationListByEventType } from '~/features/settings/automations/AutomationListByEventType';
import { eventTypeIcon } from '~/features/settings/automations/automationsContent';
import { useAutomationsByEventType } from '~/features/settings/hooks/useAutomations';
import { useConfigSettings } from '~/hooks/useConfigSettings';
import { useStoreId } from '~/providers/StoreUserProvider';

// ----- rules -----

const shippingPlusEventTypes = [
  Pegasus.EventType.quoteOrderShipment,
  Pegasus.EventType.quoteOrderShipmentParcel,
  Pegasus.EventType.quoteOrderShipmentModify,
  Pegasus.EventType.quoteOrderShipmentDeliveryEstimate,
] as const satisfies Pegasus.EventType[];

export function useShippingPlusRules() {
  const automationsByEventType = useAutomationsByEventType();
  return new Map(
    shippingPlusEventTypes.map((eventType) => [
      eventType,
      automationsByEventType.get(eventType) ?? [],
    ]),
  );
}

export function useShippingPlusRuleCount() {
  const shippingPlusRules = useShippingPlusRules();
  return [...shippingPlusRules.values()].reduce(
    (count, rules) => count + rules.length,
    0,
  );
}

function ShippingPlusAddRule() {
  const storeId = useStoreId();
  const location = useLocation();

  const categories = shippingPlusEventTypes.map((eventType) => ({
    eventType,
    label: automationsDisplayName.eventType[eventType],
    description: eventTypeDescription[eventType],
    icon: eventTypeIcon[eventType] ?? 'aiInline',
  }));

  return (
    <GridList columns={2} data={categories}>
      {({ eventType, label, description, icon }) => (
        <GridList.ItemLink
          key={eventType}
          to={{
            pathname: `/${storeId}/settings/automations/create`,
            search: new URLSearchParams({
              eventType,
              returnTo: location.pathname,
            }).toString(),
          }}
          label={label}
          description={description}
          icon={<Icon icon={icon} />}
          variant="primary"
        />
      )}
    </GridList>
  );
}

function ConfigurationAlert({
  isShippingPlusEnabled,
}: {
  isShippingPlusEnabled: boolean;
}) {
  return isShippingPlusEnabled ? (
    <Alert
      variant="warning"
      heading="Shipping Plus is on, but no Shipping Geo Zone rules have been created or enabled."
    />
  ) : (
    <Alert
      variant="tip"
      heading="To get started with Shipping Plus, create or enable a Shipping Geo Zone rule."
    />
  );
}

export default function ShippingPlusRules() {
  const { data: spSettings } = useConfigSettings(
    ({ shippingProtection }) => shippingProtection,
  );

  const shippingPlusRules = useShippingPlusRules();
  const hasShippingPlusRules = !!useShippingPlusRuleCount();

  // Reactively default to 'list' once rules are loaded, but freeze the choice
  // as soon as the user toggles either button.
  const [userView, setView] = useState<'list' | 'create' | null>(null);
  const view = userView ?? (hasShippingPlusRules ? 'list' : 'create');

  const isShippingPlusEnabled = !!spSettings?.isShippingLineProtectionOffered;

  const enabledGeoZoneRules = [...shippingPlusRules.values()]
    .flat()
    .filter((sr) => sr.hook === 'Order_Checkout_Begin')
    .filter((sr) => sr.isEnabled);

  const hasEnabledGeoZoneRules = enabledGeoZoneRules.length > 0;

  return (
    <Card>
      <Card.Line as="header">
        <Card.Heading>Automations</Card.Heading>
        <ButtonGroup horizontal>
          <Action
            onClick={() => setView('list')}
            pressed={view === 'list'}
            icon="rule"
            accessibilityLabel="View existing automations"
            disabled={!hasShippingPlusRules}
          >
            View
          </Action>
          <Action
            onClick={() => setView('create')}
            pressed={view === 'create'}
            icon="add"
            accessibilityLabel="Create a new automation"
          >
            Create
          </Action>
        </ButtonGroup>
      </Card.Line>

      {(!hasEnabledGeoZoneRules || !hasShippingPlusRules) && (
        <ConfigurationAlert isShippingPlusEnabled={isShippingPlusEnabled} />
      )}

      {view === 'create' || !hasShippingPlusRules ? (
        <ShippingPlusAddRule />
      ) : (
        [...shippingPlusRules.entries()].map(([eventType, automations]) => {
          if (!automations.length) return null;

          return (
            <AutomationListByEventType
              key={eventType}
              eventType={eventType}
              draggable
            />
          );
        })
      )}
    </Card>
  );
}
